import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, BookOpenCheckIcon, CalendarDaysIcon, CheckCircle2Icon, CircleAlertIcon, ClipboardListIcon, DownloadIcon, HomeIcon, LogOutIcon, MailIcon, MenuIcon, PackageIcon, TargetIcon, TrendingUpIcon, TruckIcon, UserIcon, XIcon } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { subscribeToOrders, type Order, type OrderLineItem } from '../../../utils/firebase';
import { getBookDownloadUrl } from '../../../utils/downloads';
import { getCheckoutUrl, getStudyUrl, type StudyProgramId } from '../../../utils/studyAccess';
import { RDReadyLogo } from '../../RDReadyLogo';
import { Study } from './Study';
const BLUE = '#2563EB';
const TEAL = '#0F9E8E';
const FONT = '"Poppins", "Inter", system-ui, sans-serif';
type AccountTab = 'overview' | 'exam-prep' | 'study' | 'profile' | 'orders';
const programs: {
  id: StudyProgramId;
  name: string;
  description: string;
  accent: string;
}[] = [{
  id: 'rd-ready-rd-exam',
  name: 'RD Exam Prep',
  description: 'A complete study system for the Registered Dietitian exam.',
  accent: BLUE
}, {
  id: 'rd-ready-cnsc-prep',
  name: 'CNSC Exam Prep',
  description: 'Focused preparation for nutrition support clinicians.',
  accent: TEAL
}];
const fieldClass = 'mt-2 w-full rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 text-sm text-[#0F172A] outline-none transition-colors placeholder:text-[#94A3B8] focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/15';
function formatDate(date: string) {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
function formatPrice(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}
function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}
export function Account() {
  const {
    user,
    profile,
    loading: authLoading,
    signOut,
    updateDisplayName,
    updateEmail,
    changePassword,
    resendVerification
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab: AccountTab = requestedTab === 'digital' ? 'exam-prep' : isAccountTab(requestedTab) ? requestedTab : 'overview';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', {
        state: {
          from: '/account'
        },
        replace: true
      });
    }
  }, [authLoading, navigate, user]);
  useEffect(() => {
    if (!user) return;
    return subscribeToOrders(user.uid, nextOrders => {
      setOrders(nextOrders);
      setOrdersLoading(false);
    });
  }, [user]);
  if (authLoading || !user) {
    return <div className="flex min-h-screen items-center justify-center bg-white" data-id="element-1629" data-mp="2pvj1y9sccizu">
        <span className="h-7 w-7 animate-spin rounded-full border-2 border-[#2563EB] border-t-transparent" data-id="element-1630" data-mp="2b63d4zcdenms" />
      </div>;
  }
  const displayName = profile?.displayName ?? user.displayName ?? 'RD Ready learner';
  const firstName = displayName.split(' ')[0];
  const selectTab = (tab: AccountTab) => {
    setSearchParams(tab === 'overview' ? {} : {
      tab
    });
    setSidebarOpen(false);
  };
  return <div className="min-h-screen bg-[#F7F9FC] text-[#0F172A]" style={{
    fontFamily: FONT
  }} data-id="element-1631" data-mp="2ytl8f4uug3ui">
      <AnimatePresence data-id="element-1632" data-mp="1ietcwj2eiggz">
        {sidebarOpen && <motion.button type="button" aria-label="Close account navigation" className="fixed inset-0 z-40 bg-[#0F172A]/35 lg:hidden" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setSidebarOpen(false)} data-id="element-1633" data-mp="1nexwwj2iptby" />}
      </AnimatePresence>

      <div className="mx-auto flex min-h-screen max-w-[1440px]" data-id="element-1634" data-mp="1oyip3oxg4a9v">
        <AccountSidebar activeTab={activeTab} firstName={firstName} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onSelect={selectTab} onSignOut={async () => {
        await signOut();
        navigate('/', {
          replace: true
        });
      }} data-id="element-1635" data-mp="2mlhseiin8ggq" />

        <main className="min-w-0 flex-1 px-5 pb-14 pt-6 sm:px-8 lg:px-12 lg:pt-10" data-id="element-1636" data-mp="27ps2486fdh67">
          <header className="mb-8 flex items-center justify-between lg:mb-10" data-id="element-1637" data-mp="1196jt058omcu">
            <button type="button" onClick={() => setSidebarOpen(true)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#E2E8F0] bg-white text-[#475569] lg:hidden" aria-label="Open account navigation" data-id="element-1638" data-mp="w7hapq1kuire">
              <MenuIcon className="h-5 w-5" data-id="element-1639" data-mp="3ht9ydu0u73i1" />
            </button>
          </header>

          <AnimatePresence mode="wait" data-id="element-1640" data-mp="3ml1tmhuxtxei">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.2
          }} data-id="element-1641" data-mp="3mmuz3tttebkx">
              {activeTab === 'overview' && <Overview firstName={firstName} examDate={profile?.examDate} onSelect={selectTab} data-id="element-1642" data-mp="267jy4qswrsa9" />}
              {activeTab === 'exam-prep' && <ExamPrep orders={orders} loading={ordersLoading} data-id="element-1643" data-mp="236zzdfo9nqo0" />}
              {activeTab === 'study' && <Study data-id="element-1644" data-mp="1z5oipzndefry" />}
              {activeTab === 'profile' && <ProfileTab displayName={displayName} email={user.email ?? ''} emailVerified={user.emailVerified} onSaveName={updateDisplayName} onSaveEmail={updateEmail} onChangePassword={changePassword} onResendVerification={resendVerification} data-id="element-1645" data-mp="2d4o8jammd0pp" />}
              {activeTab === 'orders' && <OrdersTab orders={orders} loading={ordersLoading} data-id="element-1646" data-mp="2qurpbm2qc27r" />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>;
}
function AccountSidebar({
  activeTab,
  firstName,
  isOpen,
  onClose,
  onSelect,
  onSignOut
}: {
  activeTab: AccountTab;
  firstName: string;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (tab: AccountTab) => void;
  onSignOut: () => void;
}) {
  const primary = [{
    tab: 'overview' as const,
    label: 'Overview',
    icon: HomeIcon
  }, {
    tab: 'exam-prep' as const,
    label: 'Exam Prep',
    icon: BookOpenCheckIcon
  }, {
    tab: 'study' as const,
    label: 'Study',
    icon: BookOpenCheckIcon
  }, {
    tab: 'orders' as const,
    label: 'Orders',
    icon: ClipboardListIcon
  }];
  const settings = [{
    tab: 'profile' as const,
    label: 'Profile',
    icon: UserIcon
  }];
  return <aside className={`fixed inset-y-0 left-0 z-50 flex w-[282px] flex-col border-r border-[#E2E8F0] bg-white p-5 transition-transform duration-200 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`} data-id="element-1647" data-mp="2rnpam28b27c3">
      <div className="flex items-start justify-between px-2 pb-8" data-id="element-1648" data-mp="2mpnqrf3223zk">
        <Link to="/" className="flex items-center" aria-label="RD Ready home" data-id="element-1649" data-mp="328gp5xskctb6">
          <RDReadyLogo className="h-8 w-36" data-id="element-1650" data-mp="326e1vlispea5" />
        </Link>
        <button type="button" onClick={onClose} className="p-2 text-[#64748B] lg:hidden" aria-label="Close account navigation" data-id="element-1651" data-mp="2y8g9k9tqac2j">
          <XIcon className="h-5 w-5" data-id="element-1652" data-mp="2o9qroh1u0rma" />
        </button>
      </div>

      <div className="mb-7 rounded-2xl bg-[#F7F9FC] p-4" data-id="element-1653" data-mp="2j9um4ddhks4m">
        <p className="text-xs text-[#64748B]" data-id="element-1654" data-mp="2jbfh243pqd30">Welcome back</p>
        <p className="mt-1 font-bold text-[#0F172A]" data-id="element-1655" data-mp="2eimb5cwicypc">{firstName}</p>
      </div>

      <nav aria-label="Account navigation" className="space-y-1" data-id="element-1656" data-mp="2ahkcax6i6onw">
        {primary.map(({
        tab,
        label,
        icon: Icon
      }) => <SidebarButton key={tab} active={activeTab === tab} icon={<Icon className="h-5 w-5" data-id="element-1658" data-mp="20jwe1x9ouz0z" />} label={label} onClick={() => onSelect(tab)} data-id="element-1657" data-mp="25j91aj4h2843" />)}
      </nav>

      <div className="my-7 border-t border-[#E2E8F0]" data-id="element-1659" data-mp="1vktl3b6zgj9d" />
      <p className="mb-2 px-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#94A3B8]" data-id="element-1660" data-mp="1lgv13dc97ecy">
        Account settings
      </p>
      <nav aria-label="Account settings" className="space-y-1" data-id="element-1661" data-mp="1qe4vl7ei2g7n">
        {settings.map(({
        tab,
        label,
        icon: Icon
      }) => <SidebarButton key={tab} active={activeTab === tab} icon={<Icon className="h-5 w-5" data-id="element-1663" data-mp="20cug77xs6erl" />} label={label} onClick={() => onSelect(tab)} data-id="element-1662" data-mp="1vcg7ros9p2t1" />)}
      </nav>

      <button type="button" onClick={onSignOut} className="mt-auto flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-[#64748B] transition-colors hover:bg-[#FEF2F2] hover:text-[#B91C1C]" data-id="element-1664" data-mp="17oosqdfcebo2">
        <LogOutIcon className="h-5 w-5" data-id="element-1665" data-mp="1hgb367juw6pr" /> Sign out
      </button>
    </aside>;
}
function SidebarButton({
  active,
  icon,
  label,
  onClick
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition-colors ${active ? 'bg-[#EEF2FF] text-[#2563EB]' : 'text-[#52627E] hover:bg-[#F7F9FC] hover:text-[#2563EB]'}`} data-id="element-1666" data-mp="6o7bk5rozfxt">
      {icon}
      {label}
    </button>;
}
type ReadinessStatus = 'strong' | 'building' | 'focus';
const readinessAreas: {
  title: string;
  score: number;
  status: ReadinessStatus;
  note: string;
}[] = [{
  title: 'Medical Nutrition Therapy',
  score: 84,
  status: 'strong',
  note: 'A consistent strength—keep it fresh with short review sessions.'
}, {
  title: 'Nutrition Assessment',
  score: 78,
  status: 'strong',
  note: 'Your recent practice shows a solid foundation here.'
}, {
  title: 'Clinical Management',
  score: 72,
  status: 'building',
  note: 'You are building consistency. A mixed quiz is a useful next step.'
}, {
  title: 'Enteral Nutrition',
  score: 64,
  status: 'building',
  note: 'Close the gap with targeted questions and flashcard review.'
}, {
  title: 'Parenteral Nutrition',
  score: 49,
  status: 'focus',
  note: 'This is your highest-impact study area right now.'
}];
function getExamCountdown(examDate?: string) {
  if (!examDate) return null;
  const target = new Date(`${examDate}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.ceil((target.getTime() - startOfToday.getTime()) / 86400000);
}
function formatExamDate(examDate: string) {
  return new Date(`${examDate}T00:00:00`).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}
function readinessStyle(status: ReadinessStatus) {
  return status === 'strong' ? {
    label: 'Strong',
    bar: '#0F9E8E',
    icon: CheckCircle2Icon,
    surface: 'border-[#A7E3DA] bg-[#F0FBF9]',
    text: 'text-[#0F766E]'
  } : status === 'building' ? {
    label: 'Building',
    bar: '#D97706',
    icon: TrendingUpIcon,
    surface: 'border-[#FDE68A] bg-[#FFFBEB]',
    text: 'text-[#B45309]'
  } : {
    label: 'Focus next',
    bar: '#DC2626',
    icon: CircleAlertIcon,
    surface: 'border-[#FECACA] bg-[#FEF2F2]',
    text: 'text-[#B91C1C]'
  };
}
function Overview({
  firstName,
  examDate,
  onSelect
}: {
  firstName: string;
  examDate?: string;
  onSelect: (tab: AccountTab) => void;
}) {
  const daysRemaining = getExamCountdown(examDate);
  const strongCount = readinessAreas.filter(area => area.status === 'strong').length;
  const buildingCount = readinessAreas.filter(area => area.status === 'building').length;
  const focusCount = readinessAreas.filter(area => area.status === 'focus').length;
  return <section className="mx-auto max-w-5xl" data-id="element-1667" data-mp="cjh1iv8cbu9a">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-1668" data-mp="cifnchw12rnc">
        Account overview
      </p>
      <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0F172A] sm:text-4xl" data-id="element-1669" data-mp="7kdy1f80dw3f">
        Welcome back, {firstName}.
      </h1>

      <section className="mt-7 overflow-hidden rounded-3xl bg-[#2563EB] p-6 text-white shadow-[0_12px_32px_rgba(37,99,235,.18)] sm:p-8" data-id="element-1670" data-mp="h9xeke891dv4">
        <div className="grid gap-7 md:grid-cols-[1fr_auto] md:items-end" data-id="element-1671" data-mp="3pr22d2v8if81">
          <div data-id="element-1672" data-mp="3krz5w6tblhbo">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-white/85" data-id="element-1673" data-mp="3ksh8rmahmtsk">
              <CalendarDaysIcon className="h-3.5 w-3.5" data-id="element-1674" data-mp="3uplph6lkdx1z" />
              Your exam countdown
            </span>
            {daysRemaining === null ? <>
                <h2 className="mt-5 text-2xl font-extrabold tracking-tight sm:text-3xl" data-id="element-1675" data-mp="1e2kj305nxg5q">
                  Set your exam date to shape your prep.
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/80" data-id="element-1676" data-mp="25am9ic0hupo5">
                  Add an exam-prep product at checkout and choose your date.
                  We’ll turn it into a clear countdown here.
                </p>
              </> : daysRemaining < 0 ? <>
                <h2 className="mt-5 text-2xl font-extrabold tracking-tight sm:text-3xl" data-id="element-1677" data-mp="2jadn4bn3m41m">
                  Your exam date has passed.
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/80" data-id="element-1678" data-mp="2s8z07r9swqv0">
                  Your saved exam date was {formatExamDate(examDate ?? '')}.
                  Keep practicing, then update your date with your next
                  exam-prep purchase.
                </p>
              </> : <>
                <h2 className="mt-5 text-2xl font-extrabold tracking-tight sm:text-3xl" data-id="element-1679" data-mp="370hmu7x61jnc">
                  {daysRemaining === 0 ? 'Your exam is today.' : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} until your exam.`}
                </h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/80" data-id="element-1680" data-mp="v9qe66ygwklk">
                  {daysRemaining === 0 ? 'You have done the work—take a breath and trust your preparation.' : `Your exam is scheduled for ${formatExamDate(examDate ?? '')}. Use your readiness results to choose today’s most valuable study session.`}
                </p>
              </>}
          </div>
          <div className="rounded-2xl border border-white/20 bg-white/10 px-6 py-5 text-center md:min-w-[150px]" data-id="element-1681" data-mp="19b2qm8ef642p">
            <p className="text-4xl font-extrabold tracking-tight" data-id="element-1682" data-mp="14cj343k6gk88">
              {daysRemaining === null ? '—' : Math.max(daysRemaining, 0)}
            </p>
            <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-white/75" data-id="element-1683" data-mp="21tgg9kplqjqp">
              {daysRemaining === 0 ? 'Exam day' : 'days remaining'}
            </p>
          </div>
        </div>
      </section>

      <div className="mt-9 flex flex-wrap items-end justify-between gap-4" data-id="element-1684" data-mp="27uo9241ml7">
        <div data-id="element-1685" data-mp="4aj4onm77nt">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-1686" data-mp="3hipg8t3uxo30">
            Score readiness
          </p>
          <h2 className="mt-2 text-2xl font-extrabold tracking-tight" data-id="element-1687" data-mp="3mh0r975w24mt">
            Where your practice is landing
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#5A6B7B]" data-id="element-1688" data-mp="38gg7o8s5g4s9">
            These representative results help prioritize your next session:
            green is a strength, yellow is developing, and red needs attention.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-semibold" data-id="element-1689" data-mp="33ieolnuq45gs">
          <span className="rounded-full bg-[#F0FBF9] px-3 py-1.5 text-[#0F766E]" data-id="element-1690" data-mp="33krcsir2rc2a">
            {strongCount} strong
          </span>
          <span className="rounded-full bg-[#FFFBEB] px-3 py-1.5 text-[#B45309]" data-id="element-1691" data-mp="11rti0hdqm9ep">
            {buildingCount} building
          </span>
          <span className="rounded-full bg-[#FEF2F2] px-3 py-1.5 text-[#B91C1C]" data-id="element-1692" data-mp="2ma434fism9j1">
            {focusCount} focus next
          </span>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-1693" data-mp="2vzx7owkipods">
        {readinessAreas.map(area => {
        const style = readinessStyle(area.status);
        const Icon = style.icon;
        return <article key={area.title} className={`rounded-2xl border p-5 ${style.surface}`} data-id="element-1694" data-mp="34zalg1focz4b">
              <div className="flex items-start justify-between gap-4" data-id="element-1695" data-mp="300qypyt9bjaw">
                <div data-id="element-1696" data-mp="3ex8b8td6n3z1">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${style.text}`} data-id="element-1697" data-mp="3exrjfid16dzv">
                    <Icon className="h-4 w-4" aria-hidden="true" data-id="element-1698" data-mp="1kjyvwkxygo8v" />
                    {style.label}
                  </span>
                  <h3 className="mt-3 text-lg font-bold text-[#0F172A]" data-id="element-1699" data-mp="1flxahtd24cu8">
                    {area.title}
                  </h3>
                </div>
                <span className={`text-2xl font-extrabold ${style.text}`} data-id="element-1700" data-mp="tq5jpmjcsbe5">
                  {area.score}%
                </span>
              </div>
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/80" data-id="element-1701" data-mp="13iugrce5pmpy">
                <div className="h-full rounded-full" style={{
              width: `${area.score}%`,
              backgroundColor: style.bar
            }} data-id="element-1702" data-mp="18he2pcsr2yib" />
              </div>
              <p className="mt-3 text-xs leading-relaxed text-[#52627E]" data-id="element-1703" data-mp="fsgww95q6u6g">
                {area.note}
              </p>
            </article>;
      })}
      </div>

      <div className="mt-7 flex flex-col gap-4 rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-1704" data-mp="otyejvys73x9">
        <div data-id="element-1705" data-mp="1w8t8xgnndzyu">
          <div className="flex items-center gap-2" data-id="element-1706" data-mp="207scoxxn7qop">
            <TargetIcon className="h-5 w-5 text-[#2563EB]" aria-hidden="true" data-id="element-1707" data-mp="206ha31fxooz6" />
            <h2 className="font-bold text-[#0F172A]" data-id="element-1708" data-mp="3kp1jmiqdyo73">
              Make your next session count
            </h2>
          </div>
          <p className="mt-2 text-sm text-[#64748B]" data-id="element-1709" data-mp="3gpauskkf6i57">
            Start with Parenteral Nutrition, then use a mixed quiz to reinforce
            developing topics.
          </p>
        </div>
        <button type="button" onClick={() => onSelect('exam-prep')} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-id="element-1710" data-mp="36y6ftkuquxad">
          View programs <ArrowRightIcon className="h-4 w-4" data-id="element-1711" data-mp="2o1oi8mt7s367" />
        </button>
      </div>
    </section>;
}
function ExamPrep({
  orders,
  loading
}: {
  orders: Order[];
  loading: boolean;
}) {
  return <section className="mx-auto max-w-5xl" data-id="element-1712" data-mp="m9s7hfevexch">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2563EB]" data-id="element-1713" data-mp="1fj20iuhl4j53">
        Your library
      </p>
      <h1 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl" data-id="element-1714" data-mp="1ajfblnse9egg">
        Exam Prep
      </h1>
      <p className="mt-3 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-1715" data-mp="15sp9qkj6ftqi">
        Each program is a one-time $150 purchase, tied to the RD Ready account
        used at checkout.
      </p>
      <div className="mt-8 grid gap-6 md:grid-cols-2" data-id="element-1716" data-mp="vusy7rmbuwrv">
        {programs.map(program => {
        const unlocked = hasPaidProgram(orders, program.id);
        return <article key={program.id} className="rounded-3xl border border-[#E2E8F0] bg-white p-7 shadow-[0_2px_16px_rgba(15,23,42,.04)]" data-id="element-1717" data-mp="rvtwefvw7g4n">
              <span className="inline-flex rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-white" style={{
            backgroundColor: program.accent
          }} data-id="element-1718" data-mp="hx4dcjs9fpmt">
                {loading ? 'Checking access' : unlocked ? 'Unlocked' : 'One-time · $150'}
              </span>
              <h2 className="mt-6 text-2xl font-extrabold" data-id="element-1719" data-mp="2tiwszxz3pgok">{program.name}</h2>
              <p className="mt-3 text-sm leading-relaxed text-[#5A6B7B]" data-id="element-1720" data-mp="2tkzh2cgp0zqn">
                {program.description}
              </p>
              <div className="mt-7 border-t border-[#E2E8F0] pt-5" data-id="element-1721" data-mp="20uhdlac24wcp">
                <p className="text-xs font-semibold text-[#64748B]" data-id="element-1722" data-mp="20wc2ezqg98m2">
                  Questions, flashcards, and focused topic review.
                </p>
                <Link to={unlocked ? getStudyUrl(program.id) : getCheckoutUrl(program.id)} className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5" style={{
              backgroundColor: program.accent
            }} data-id="element-1723" data-mp="2arccvvg5n0s1">
                  {unlocked ? 'Open study' : 'Unlock for $150'}
                  <ArrowRightIcon className="h-4 w-4" data-id="element-1724" data-mp="1m7f70kj2uwhv" />
                </Link>
              </div>
            </article>;
      })}
      </div>
    </section>;
}
function hasPaidProgram(orders: Order[], programId: StudyProgramId) {
  return orders.some(order => order.status === 'paid' && order.lineItems.some(item => item.productId === programId));
}
function ProfileTab({
  displayName,
  email,
  emailVerified,
  onSaveName,
  onSaveEmail,
  onChangePassword,
  onResendVerification
}: {
  displayName: string;
  email: string;
  emailVerified: boolean;
  onSaveName: (name: string) => Promise<void>;
  onSaveEmail: (email: string, password: string) => Promise<void>;
  onChangePassword: (current: string, next: string) => Promise<void>;
  onResendVerification: () => Promise<void>;
}) {
  const [name, setName] = useState(displayName);
  const [newEmail, setNewEmail] = useState(email);
  const [emailPassword, setEmailPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<'name' | 'email' | 'password' | 'verify' | null>(null);
  const run = async (action: 'name' | 'email' | 'password' | 'verify', task: () => Promise<void>, message: string) => {
    setSaving(action);
    setStatus(null);
    setError(null);
    try {
      await task();
      setStatus(message);
    } catch (err) {
      setError(getErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setSaving(null);
    }
  };
  return <section className="mx-auto max-w-2xl" data-id="element-1725" data-mp="3932bvv4sdc5m">
      <h1 className="text-3xl font-extrabold tracking-tight" data-id="element-1726" data-mp="3p9f88g3vg73v">Profile</h1>
      <p className="mt-3 text-sm text-[#5A6B7B]" data-id="element-1727" data-mp="3paqd6j91zkwk">
        Manage the details and security settings for your RD Ready account.
      </p>

      <div aria-live="polite" className="mt-6" data-id="element-1728" data-mp="3fiaufjpxmsam">
        {status && <p className="rounded-xl border border-[#C7EFE8] bg-[#F0FBF9] p-4 text-sm font-medium text-[#0F766E]" data-id="element-1729" data-mp="3ak0zna84dtpj">
            {status}
          </p>}
        {error && <p className="rounded-xl border border-[#FBD5D0] bg-[#FEF2F2] p-4 text-sm font-medium text-[#B91C1C]" data-id="element-1730" data-mp="c0arwk35bf4e">
            {error}
          </p>}
      </div>

      <form onSubmit={event => {
      event.preventDefault();
      run('name', () => onSaveName(name), 'Your name has been updated.');
    }} className="mt-6 rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-8" data-id="element-1731" data-mp="22wbull4wscm">
        <h2 className="text-lg font-bold" data-id="element-1732" data-mp="2hvbwl5ni7eb5">Personal details</h2>
        <label className="mt-5 block text-sm font-semibold" data-id="element-1733" data-mp="2hu0oitn0zke8">
          Full name
          <input value={name} onChange={event => setName(event.target.value)} required className={fieldClass} autoComplete="name" data-id="element-1734" data-mp="xem3apddoj0z" />
        </label>
        <button disabled={saving !== null} className="mt-6 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-id="element-1735" data-mp="17a60df3ohnrb">
          {saving === 'name' ? 'Saving…' : 'Save name'}
        </button>
      </form>

      <form onSubmit={event => {
      event.preventDefault();
      run('email', () => onSaveEmail(newEmail, emailPassword), 'Check your new inbox to confirm this email change.');
    }} className="mt-5 rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-8" data-id="element-1736" data-mp="1l1uc3ha0ma7r">
        <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-1737" data-mp="3rvoarw7pez0r">
          <div data-id="element-1738" data-mp="nagz8a5pr10">
            <h2 className="text-lg font-bold" data-id="element-1739" data-mp="p4u40f6seuj">Email address</h2>
            <p className="mt-1 text-sm text-[#64748B]" data-id="element-1740" data-mp="3osi5morwqnxv">
              Changing your email requires your current password.
            </p>
          </div>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${emailVerified ? 'bg-[#F0FBF9] text-[#0F766E]' : 'bg-[#FFF7ED] text-[#C2410C]'}`} data-id="element-1741" data-mp="2kiltofiuzys">
            {emailVerified ? 'Verified' : 'Not verified'}
          </span>
        </div>
        <label className="mt-5 block text-sm font-semibold" data-id="element-1742" data-mp="bjdtz624mk4q">
          Email address
          <input type="email" value={newEmail} onChange={event => setNewEmail(event.target.value)} required className={fieldClass} autoComplete="email" data-id="element-1743" data-mp="7j3mfoyorlnr" />
        </label>
        <label className="mt-4 block text-sm font-semibold" data-id="element-1744" data-mp="v8xwjyrmew4b">
          Current password
          <input type="password" value={emailPassword} onChange={event => setEmailPassword(event.target.value)} required className={fieldClass} autoComplete="current-password" data-id="element-1745" data-mp="qa38ahavwfct" />
        </label>
        <div className="mt-6 flex flex-wrap gap-3" data-id="element-1746" data-mp="1dvdsaju64hmm">
          <button disabled={saving !== null || newEmail.trim().toLowerCase() === email.toLowerCase()} className="rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-id="element-1747" data-mp="zyi9w0s11dtw">
            {saving === 'email' ? 'Updating…' : 'Update email'}
          </button>
          {!emailVerified && <button type="button" disabled={saving !== null} onClick={() => run('verify', onResendVerification, 'A verification email has been sent.')} className="rounded-full border border-[#CBD5E1] px-5 py-2.5 text-sm font-semibold text-[#475569] transition-colors hover:border-[#2563EB] hover:text-[#2563EB] disabled:opacity-60" data-id="element-1748" data-mp="h80jw3ykwwc8">
              {saving === 'verify' ? 'Sending…' : 'Resend verification'}
            </button>}
        </div>
      </form>

      <form onSubmit={event => {
      event.preventDefault();
      if (newPassword !== confirmPassword) {
        setError('New passwords do not match.');
        return;
      }
      run('password', async () => {
        await onChangePassword(currentPassword, newPassword);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }, 'Your password has been updated.');
    }} className="mt-5 rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-8" data-id="element-1749" data-mp="inmpzlbq109h">
        <h2 className="text-lg font-bold" data-id="element-1750" data-mp="3h73c102ontuq">Password</h2>
        <label className="mt-5 block text-sm font-semibold" data-id="element-1751" data-mp="3h8eemwke6vk9">
          Current password
          <input type="password" value={currentPassword} onChange={event => setCurrentPassword(event.target.value)} required className={fieldClass} autoComplete="current-password" data-id="element-1752" data-mp="3724ugujrv6bm" />
        </label>
        <label className="mt-4 block text-sm font-semibold" data-id="element-1753" data-mp="3m0yra8wxhdfz">
          New password
          <input type="password" minLength={6} value={newPassword} onChange={event => setNewPassword(event.target.value)} required className={fieldClass} autoComplete="new-password" data-id="element-1754" data-mp="2ocx5nvsh4d4t" />
        </label>
        <label className="mt-4 block text-sm font-semibold" data-id="element-1755" data-mp="331b47uiuxffk">
          Confirm new password
          <input type="password" minLength={6} value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} required className={fieldClass} autoComplete="new-password" data-id="element-1756" data-mp="25m6yvvzs866o" />
        </label>
        <button disabled={saving !== null} className="mt-6 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-id="element-1757" data-mp="1ntxs7m9l64vh">
          {saving === 'password' ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </section>;
}
function AddressesTab({
  addresses,
  onSave
}: {
  addresses: Address[];
  onSave: (addresses: Address[]) => Promise<void>;
}) {
  const [editing, setEditing] = useState<Address | null>(null);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const saveAddress = async (draft: AddressDraft) => {
    setBusy(true);
    try {
      const next = editing ? addresses.map(address => address.id === editing.id ? {
        ...draft,
        id: editing.id,
        isDefault: address.isDefault
      } : address) : [...addresses, {
        ...draft,
        id: makeAddressId(),
        isDefault: addresses.length === 0
      }];
      await onSave(next);
      setEditing(null);
      setAdding(false);
    } finally {
      setBusy(false);
    }
  };
  const setDefault = async (id: string) => {
    setBusy(true);
    try {
      await onSave(addresses.map(address => ({
        ...address,
        isDefault: address.id === id
      })));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id: string) => {
    setBusy(true);
    try {
      const remaining = addresses.filter(address => address.id !== id);
      if (remaining.length && !remaining.some(address => address.isDefault)) remaining[0].isDefault = true;
      await onSave(remaining);
    } finally {
      setBusy(false);
    }
  };
  return <section className="mx-auto max-w-4xl" data-id="element-1758" data-mp="zam4qmrpokiu">
      <div className="flex items-center justify-between gap-4" data-id="element-1759" data-mp="3t1aufgbb2del">
        <div data-id="element-1760" data-mp="3syy16703kgm8">
          <h1 className="text-3xl font-extrabold tracking-tight" data-id="element-1761" data-mp="3sz7ti18m6zv3">Addresses</h1>
          <p className="mt-2 text-sm text-[#5A6B7B]" data-id="element-1762" data-mp="xhz9fjahamu">
            Saved addresses for legacy shop orders.
          </p>
        </div>
        {!adding && !editing && <button type="button" onClick={() => setAdding(true)} className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white" data-id="element-1763" data-mp="ao35np57fjgb">
            <PlusIcon className="h-4 w-4" data-id="element-1764" data-mp="knbyufs8wu1m" /> Add address
          </button>}
      </div>

      {(adding || editing) && <AddressForm initial={editing} busy={busy} onCancel={() => {
      setAdding(false);
      setEditing(null);
    }} onSave={saveAddress} data-id="element-1765" data-mp="tlnmqa1q9dqd" />}

      {!adding && !editing && <div className="mt-7 grid gap-4 sm:grid-cols-2" data-id="element-1766" data-mp="2vdsixxq9ex6w">
          {addresses.length ? addresses.map(address => <article key={address.id} className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-id="element-1767" data-mp="2j31a5kvdp5yz">
                <div className="flex items-start justify-between gap-3" data-id="element-1768" data-mp="2e4q29fon95je">
                  <p className="font-bold" data-id="element-1769" data-mp="295dcam2h7icl">{address.recipientName}</p>
                  {address.isDefault && <span className="rounded-full bg-[#EEF4FF] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#2563EB]" data-id="element-1770" data-mp="36rkke5xznstp">
                      Default
                    </span>}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#64748B]" data-id="element-1771" data-mp="2wwitp5o2eip0">
                  {address.line1}
                  {address.line2 && <>
                      <br data-id="element-1772" data-mp="1lg2d390rxw0k" />
                      {address.line2}
                    </>}
                  <br data-id="element-1773" data-mp="1hc4cetvswd5e" />
                  {address.city}, {address.state} {address.postalCode}
                  <br data-id="element-1774" data-mp="358snn4wkkeym" />
                  {address.country}
                </p>
                {address.deliveryInstructions && <p className="mt-4 border-t border-[#E2E8F0] pt-4 text-xs leading-relaxed text-[#64748B]" data-id="element-1775" data-mp="3a2wwxutb4z2v">
                    <strong className="text-[#475569]" data-id="element-1776" data-mp="30badjb6tftp1">
                      Delivery instructions:
                    </strong>{' '}
                    {address.deliveryInstructions}
                  </p>}
                <div className="mt-5 flex flex-wrap gap-x-4 gap-y-3 border-t border-[#E2E8F0] pt-4" data-id="element-1777" data-mp="3t07tv8rgvgf5">
                  <button type="button" onClick={() => setEditing(address)} disabled={busy} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#475569] hover:text-[#2563EB]" data-id="element-1778" data-mp="3f0ybq3jpac8j">
                    <Edit2Icon className="h-4 w-4" data-id="element-1779" data-mp="27mvazo4vf3ul" /> Edit
                  </button>
                  {!address.isDefault && <button type="button" onClick={() => setDefault(address.id)} disabled={busy} className="text-sm font-semibold text-[#2563EB] disabled:opacity-60" data-id="element-1780" data-mp="201j0aeprhpli">
                      Set as default
                    </button>}
                  <button type="button" onClick={() => remove(address.id)} disabled={busy} className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-[#B91C1C] disabled:opacity-60" data-id="element-1781" data-mp="1hc2xtluinn2g">
                    <Trash2Icon className="h-4 w-4" data-id="element-1782" data-mp="1mb5scccveaw6" /> Delete
                  </button>
                </div>
              </article>) : <div className="col-span-full rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-10 text-center" data-id="element-1783" data-mp="yk0cvyaudkme">
              <MapPinIcon className="mx-auto h-8 w-8 text-[#94A3B8]" data-id="element-1784" data-mp="1jb5qmx35ykev" />
              <p className="mt-4 font-semibold" data-id="element-1785" data-mp="1j92zuau6sn93">No saved addresses yet.</p>
              <button type="button" onClick={() => setAdding(true)} className="mt-5 text-sm font-semibold text-[#2563EB]" data-id="element-1786" data-mp="1nbg2gqdn051n">
                Add an address
              </button>
            </div>}
        </div>}
    </section>;
}
function AddressForm({
  initial,
  busy,
  onCancel,
  onSave
}: {
  initial: Address | null;
  busy: boolean;
  onCancel: () => void;
  onSave: (address: AddressDraft) => Promise<void>;
}) {
  const [recipientName, setRecipientName] = useState(initial?.recipientName ?? '');
  const [line1, setLine1] = useState(initial?.line1 ?? '');
  const [line2, setLine2] = useState(initial?.line2 ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [state, setState] = useState(initial?.state ?? '');
  const [postalCode, setPostalCode] = useState(initial?.postalCode ?? '');
  const [country, setCountry] = useState(initial?.country ?? 'United States');
  const [deliveryInstructions, setDeliveryInstructions] = useState(initial?.deliveryInstructions ?? '');
  return <form onSubmit={event => {
    event.preventDefault();
    onSave({
      recipientName: recipientName.trim(),
      line1: line1.trim(),
      line2: line2.trim(),
      city: city.trim(),
      state: state.trim(),
      postalCode: postalCode.trim(),
      country: country.trim(),
      deliveryInstructions: deliveryInstructions.trim()
    });
  }} className="mt-7 rounded-2xl border border-[#E2E8F0] bg-white p-6 sm:p-8" data-id="element-1787" data-mp="4crwhugu3gi0">
      <h2 className="text-lg font-bold" data-id="element-1788" data-mp="3cmtgqvjk5cln">
        {initial ? 'Edit address' : 'Add address'}
      </h2>
      <div className="mt-5 grid gap-4 sm:grid-cols-2" data-id="element-1789" data-mp="1fsq19m4zbgdf">
        <label className="text-sm font-semibold" data-id="element-1790" data-mp="1auetdgy8vfxu">
          Recipient full name
          <input required value={recipientName} onChange={event => setRecipientName(event.target.value)} className={fieldClass} data-id="element-1791" data-mp="1asc08o1sp6ow" />
        </label>
        <label className="text-sm font-semibold" data-id="element-1792" data-mp="awh7g4wuwkfn">
          Address line 1
          <input required value={line1} onChange={event => setLine1(event.target.value)} className={fieldClass} data-id="element-1793" data-mp="auelpx2qldgq" />
        </label>
        <label className="text-sm font-semibold" data-id="element-1794" data-mp="x03pt160gmb">
          Address line 2{' '}
          <span className="font-normal text-[#94A3B8]" data-id="element-1795" data-mp="5y7b3qzebdsg">(optional)</span>
          <input value={line2} onChange={event => setLine2(event.target.value)} className={fieldClass} data-id="element-1796" data-mp="3o3ixdfsiph9v" />
        </label>
        <label className="text-sm font-semibold" data-id="element-1797" data-mp="3s6fl1kwx03ko">
          City
          <input required value={city} onChange={event => setCity(event.target.value)} className={fieldClass} data-id="element-1798" data-mp="1mc5x541hvxk5" />
        </label>
        <label className="text-sm font-semibold" data-id="element-1799" data-mp="3aw3daxmv6401">
          State / province
          <input required value={state} onChange={event => setState(event.target.value)} className={fieldClass} data-id="element-1800" data-mp="36uhs8p06ejer" />
        </label>
        <label className="text-sm font-semibold" data-id="element-1801" data-mp="3ftv3nn7v1i24">
          ZIP / postal code
          <input required value={postalCode} onChange={event => setPostalCode(event.target.value)} className={fieldClass} data-id="element-1802" data-mp="3uj9e6yn6a5j3" />
        </label>
        <label className="text-sm font-semibold sm:col-span-2" data-id="element-1803" data-mp="7el5urb06z05">
          Country
          <input required value={country} onChange={event => setCountry(event.target.value)} className={fieldClass} data-id="element-1804" data-mp="7ch2qbkxi40x" />
        </label>
        <label className="text-sm font-semibold sm:col-span-2" data-id="element-1805" data-mp="2966jqx5z3s24">
          Delivery instructions{' '}
          <span className="font-normal text-[#94A3B8]" data-id="element-1806" data-mp="248y3510h3w2z">(optional)</span>
          <textarea rows={3} value={deliveryInstructions} onChange={event => setDeliveryInstructions(event.target.value)} className={`${fieldClass} resize-none`} data-id="element-1807" data-mp="ke6x1rywucqq" />
        </label>
      </div>
      <div className="mt-6 flex flex-wrap gap-3" data-id="element-1808" data-mp="oez7gojiqnol">
        <button disabled={busy} className="rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" data-id="element-1809" data-mp="oe7n69vdgcdm">
          {busy ? 'Saving…' : 'Save address'}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className="rounded-full border border-[#CBD5E1] px-5 py-2.5 text-sm font-semibold text-[#475569]" data-id="element-1810" data-mp="yb2hshoixsmm">
          Cancel
        </button>
      </div>
    </form>;
}
function OrdersTab({
  orders,
  loading
}: {
  orders: Order[];
  loading: boolean;
}) {
  return <section className="mx-auto max-w-4xl" data-id="element-1811" data-mp="1h4zccne0s0rg">
      <h1 className="text-3xl font-extrabold tracking-tight" data-id="element-1812" data-mp="1h1tqdh0otezy">Orders</h1>
      <p className="mt-3 text-sm text-[#5A6B7B]" data-id="element-1813" data-mp="20q7xspmv7pq7">
        Your purchase history from the RD Ready store.
      </p>
      <div className="mt-8 space-y-4" data-id="element-1814" data-mp="25mghmgc0c73p">
        {loading ? <div className="rounded-2xl bg-white p-10 text-center" data-id="element-1815" data-mp="1qqh24kmkj4oj">
            <span className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-[#2563EB] border-t-transparent" data-id="element-1816" data-mp="1vpu123qps27h" />
          </div> : orders.length ? orders.map(order => <OrderCard key={order.id} order={order} data-id="element-1817" data-mp="1mpf2w3snismk" />) : <div className="rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-10 text-center" data-id="element-1818" data-mp="1mrrs920qo59n">
            <PackageIcon className="mx-auto h-8 w-8 text-[#94A3B8]" data-id="element-1819" data-mp="17yocn06pk1lk" />
            <p className="mt-4 font-semibold" data-id="element-1820" data-mp="1d0nbpxvf0ccp">No orders yet.</p>
            <p className="mt-2 text-sm text-[#64748B]" data-id="element-1821" data-mp="yzkr7pk2ja3w">
              Purchase an exam-prep program from the Exam Prep tab to unlock
              study access.
            </p>
          </div>}
      </div>
    </section>;
}
function OrderCard({
  order
}: {
  order: Order;
}) {
  const [downloadState, setDownloadState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const paid = order.status === 'paid';
  const openStudy = (item: OrderLineItem) => item.productId === 'rd-ready-rd-exam' || item.productId === 'rd-ready-cnsc-prep';
  const download = async () => {
    setDownloadState('loading');
    setDownloadError(null);
    try {
      const {
        url
      } = await getBookDownloadUrl(order.id);
      window.open(url, '_blank', 'noopener,noreferrer');
      setDownloadState('idle');
    } catch (err) {
      setDownloadState('error');
      setDownloadError(getErrorMessage(err, 'Download failed. Please try again.'));
    }
  };
  return <article className="rounded-2xl border border-[#E2E8F0] bg-white p-6" data-id="element-1822" data-mp="23b7csahh0rup">
      <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-1823" data-mp="1z781q7eyml8q">
        <div data-id="element-1824" data-mp="eqs2bpt02h9j">
          <p className="text-xs font-bold uppercase tracking-wider text-[#94A3B8]" data-id="element-1825" data-mp="er1kir7szk4o">
            Order {order.id.slice(0, 8).toUpperCase()}
          </p>
          <p className="mt-2 font-bold" data-id="element-1826" data-mp="iphc78etxgof">{formatDate(order.createdAt)}</p>
        </div>
        <span className="rounded-full bg-[#F0FBF9] px-3 py-1 text-xs font-semibold capitalize text-[#0F766E]" data-id="element-1827" data-mp="noty9qxvmjpy">
          {order.fulfillmentStatus.replace('_', ' ')}
        </span>
      </div>
      {(order.shippingAddress || order.trackingNumber) && <div className="mt-5 grid gap-4 rounded-xl bg-[#F7F9FC] p-4 text-sm text-[#64748B] sm:grid-cols-2" data-id="element-1828" data-mp="12f1hw9zme8s8">
          {order.shippingAddress && <p data-id="element-1829" data-mp="16fbmpjboet5i">
              <strong className="text-[#334155]" data-id="element-1830" data-mp="1bgivnlpk1ydr">Shipping:</strong>
              <br data-id="element-1831" data-mp="1bfpy9bchkl8a" />
              {order.shippingAddress.line1}
              {order.shippingAddress.line2 && <>
                  <br data-id="element-1832" data-mp="3d7w24f768ebx" />
                  {order.shippingAddress.line2}
                </>}
              <br data-id="element-1833" data-mp="2hxfmm1i2v8ts" />
              {order.shippingAddress.city}, {order.shippingAddress.state}{' '}
              {order.shippingAddress.postalCode}
            </p>}
          {order.trackingNumber && <p data-id="element-1834" data-mp="2rwexzru89ikh">
              <strong className="text-[#334155]" data-id="element-1835" data-mp="2ruu5e7rh49p9">Tracking:</strong>
              <br data-id="element-1836" data-mp="2mx21efdk0x4p" />
              <span className="inline-flex items-center gap-1.5" data-id="element-1837" data-mp="2mwistp9snl3c">
                <TruckIcon className="h-4 w-4" data-id="element-1838" data-mp="1z77ojxt8j8kg" />
                {order.trackingCarrier ? `${order.trackingCarrier} · ` : ''}
                {order.trackingNumber}
              </span>
            </p>}
        </div>}
      <ul className="mt-5 divide-y divide-[#E2E8F0] border-t border-[#E2E8F0] text-sm" data-id="element-1839" data-mp="28x9dertewv0f">
        {order.lineItems.map((item, index) => <li key={`${item.productId}-${index}`} className="py-4" data-id="element-1840" data-mp="244zqzlriad2w" data-mpi={index}>
            <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1841" data-mp="1gfonvxmoo6ll">
              <div data-id="element-1842" data-mp="1bi59mxpzjttn">
                <p className="font-semibold text-[#0F172A]" data-id="element-1843" data-mp="1bgkh1dn8ekyf">
                  {item.name} × {item.quantity}
                </p>
                <p className="mt-1 text-[#64748B]" data-id="element-1844" data-mp="3n3nywn7vdbp6">
                  {item.type === 'book' ? 'Digital item' : 'Physical item'} ·{' '}
                  {formatPrice(item.unitPrice * item.quantity)}
                </p>
              </div>
              {paid && openStudy(item) && <Link to={`/account?tab=study&product=${item.productId}`} className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-4 py-2 text-xs font-semibold text-white" data-id="element-1845" data-mp="3i6fdmdrfcrjp">
                  Open study <ArrowRightIcon className="h-3.5 w-3.5" data-id="element-1846" data-mp="po1o50q0gvo" />
                </Link>}
              {paid && item.type === 'book' && !openStudy(item) && <button type="button" onClick={download} disabled={downloadState === 'loading'} className="inline-flex items-center gap-2 rounded-full border border-[#CBD5E1] px-4 py-2 text-xs font-semibold text-[#475569] disabled:opacity-60" data-id="element-1847" data-mp="4t2kf3g1nbct">
                  <DownloadIcon className="h-3.5 w-3.5" data-id="element-1848" data-mp="qzojmxle41y2" />
                  {downloadState === 'loading' ? 'Preparing…' : 'Download'}
                </button>}
            </div>
          </li>)}
      </ul>
      {downloadError && <p role="alert" className="mt-4 text-sm text-[#B91C1C]" data-id="element-1849" data-mp="h9bne847qkc3">
          {downloadError}
        </p>}
      <p className="mt-4 border-t border-[#E2E8F0] pt-4 text-right text-sm font-bold" data-id="element-1850" data-mp="d5x67e4jfxx2">
        Total {formatPrice(order.total)}
      </p>
    </article>;
}
function isAccountTab(tab: string | null): tab is AccountTab {
  return tab === 'overview' || tab === 'exam-prep' || tab === 'study' || tab === 'profile' || tab === 'orders';
}